<div class="calendar-container">
  <!-- Sidebar -->
  <aside class="calendar-sidebar">
    <div class="sidebar-header">
      <h2>
        <i class="fas fa-calendar-alt"></i>
        Calendar
      </h2>
      <button class="add-event-btn" (click)="openAddEventModal()">
        <i class="fas fa-plus"></i>
        New Event
      </button>
    </div>

    <!-- Today's Events -->
    <div class="sidebar-section">
      <h3 class="section-title">
        <i class="fas fa-clock"></i>
        Today's Events
      </h3>
      @if (todayEvents().length === 0) {
        <div class="no-events-wrapper">
          <i class="fas fa-calendar-check"></i>
          <p class="no-events">No events today</p>
        </div>
      } @else {
        <div class="event-list">
          @for (event of todayEvents(); track trackByEventId($index, event)) {
            <div
              class="sidebar-event"
              [style.border-left-color]="event.color?.background"
              (click)="openEventDetails(event)"
            >
              <span class="event-time">{{ formatEventTime(event) }}</span>
              <span class="event-title">{{ event.title }}</span>
            </div>
          }
        </div>
      }
    </div>

    <!-- Upcoming Events -->
    <div class="sidebar-section">
      <h3 class="section-title">
        <i class="fas fa-calendar-week"></i>
        Upcoming
      </h3>
      @if (upcomingEvents().length === 0) {
        <div class="no-events-wrapper">
          <i class="fas fa-calendar-week"></i>
          <p class="no-events">No upcoming events</p>
        </div>
      } @else {
        <div class="event-list">
          @for (event of upcomingEvents(); track trackByEventId($index, event)) {
            <div
              class="sidebar-event"
              [style.border-left-color]="event.color?.background"
              (click)="openEventDetails(event)"
            >
              <span class="event-date">{{ event.startDate | date:'MMM d' }}</span>
              <span class="event-title">{{ event.title }}</span>
            </div>
          }
        </div>
      }
    </div>

    <!-- Actions -->
    <div class="sidebar-actions">
      <div class="export-dropdown-container">
        <button class="action-btn" (click)="toggleExportMenu()">
          <i class="fas fa-download"></i>
          Export
          <i class="fas fa-chevron-down dropdown-icon" [class.open]="showExportMenu"></i>
        </button>
        @if (showExportMenu) {
          <div class="export-dropdown" @fadeIn>
            <button class="dropdown-item" (click)="exportEvents()">
              <i class="fas fa-file-code"></i>
              Export as JSON
            </button>
            <button class="dropdown-item" (click)="exportToICS()">
              <i class="fas fa-calendar-alt"></i>
              Export as ICS
            </button>
            <div class="dropdown-divider"></div>
            <span class="dropdown-label">Add All Events to:</span>
            @if (events.length > 0) {
              <button class="dropdown-item" (click)="addToGoogleCalendar(events[0])">
                <i class="fab fa-google" style="color: #4285f4;"></i>
                Google Calendar
              </button>
              <button class="dropdown-item" (click)="addToYahooCalendar(events[0])">
                <i class="fab fa-yahoo" style="color: #6001d2;"></i>
                Yahoo Calendar
              </button>
              <button class="dropdown-item" (click)="addToOutlookCalendar(events[0])">
                <i class="fab fa-microsoft" style="color: #0078d4;"></i>
                Outlook.com
              </button>
              <button class="dropdown-item" (click)="addToOffice365Calendar(events[0])">
                <i class="fas fa-briefcase" style="color: #d83b01;"></i>
                Office 365
              </button>
            } @else {
              <span class="dropdown-hint">No events to export</span>
            }
          </div>
        }
      </div>
      <button class="action-btn" (click)="importEvents()">
        <i class="fas fa-upload"></i>
        Import
      </button>
    </div>
  </aside>

  <!-- Main Calendar Area -->
  <main class="calendar-main">
    <!-- Header -->
    <header class="calendar-header">
      <div class="header-left">
        <button class="today-btn" (click)="goToToday()">Today</button>
        <div class="nav-buttons">
          <button class="nav-btn" (click)="navigate('prev')">
            <i class="fas fa-chevron-left"></i>
          </button>
          <button class="nav-btn" (click)="navigate('next')">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        <h1 class="current-date">{{ currentMontShorthName() }}</h1>
      </div>
      <div class="header-right">
        <div class="view-dropdown-container">
          <button class="view-dropdown-btn" (click)="toggleViewDropdown()">
            @if (currentView() === 'month') {
              <i class="fas fa-th"></i>
              Month
            } @else if (currentView() === 'week') {
              <i class="fas fa-calendar-week"></i>
              Week
            } @else {
              <i class="fas fa-list"></i>
              List
            }
            <i class="fas fa-chevron-down dropdown-icon" [class.open]="showViewDropdown"></i>
          </button>
          @if (showViewDropdown) {
            <ul class="view-dropdown" @fadeIn (click)="$event.stopPropagation()">
              <li>
                <button
                  class="view-dropdown-item"
                  [class.active]="currentView() === 'month'"
                  (click)="setView('month')"
                >
                  <i class="fas fa-th"></i>
                  Month
                </button>
              </li>
              <li>
                <button
                  class="view-dropdown-item"
                  [class.active]="currentView() === 'week'"
                  (click)="setView('week')"
                >
                  <i class="fas fa-calendar-week"></i>
                  Week
                </button>
              </li>
              <li>
                <button
                  class="view-dropdown-item"
                  [class.active]="currentView() === 'list'"
                  (click)="setView('list')"
                >
                  <i class="fas fa-list"></i>
                  List
                </button>
              </li>
            </ul>
          }
        </div>
      </div>
    </header>

    <!-- Month View -->
    @if (currentView() === 'month') {
      <div class="month-view" @fadeIn>
        <div class="weekday-header">
          @for (day of weekDays; track day) {
            <div class="weekday">{{ day }}</div>
          }
        </div>
        <div class="days-grid">
          @for (day of calendarDays; track trackByDate($index, day)) {
            <div
              class="day-cell"
              [class.other-month]="!day.isCurrentMonth"
              [class.today]="day.isToday"
              [class.selected]="day.isSelected"
              (click)="selectDate(day)"
              (dblclick)="openAddEventModal(day.date)"
            >
              <div class="day-number-wrapper">
                <span class="day-number">{{ day.date.getDate() }}</span>
              </div>
              <div class="day-events">
                @for (event of day.events.slice(0, 3); track trackByEventId($index, event)) {
                  <div
                    class="day-event"
                    [style.background-color]="event.color?.background"
                    [style.color]="event.color?.foreground"
                    (click)="openEventDetails(event); $event.stopPropagation()"
                  >
                    {{ event.title }}
                  </div>
                }
                @if (day.events.length > 3) {
                  <div class="more-events" (click)="selectDate(day); $event.stopPropagation()">
                    <i class="fas fa-ellipsis-h"></i>
                    <span>+{{ day.events.length - 3 }} more</span>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- Week View -->
    @if (currentView() === 'week') {
      <div class="week-view" @fadeIn>
        <div class="week-header">
          <div class="time-gutter"></div>
          @for (date of getWeekDates(); track $index) {
            <div class="week-day-header" [class.today]="isToday(date)">
              <span class="day-name">{{ date | date:'EEE' }}</span>
              <span class="day-date">{{ date | date:'d' }}</span>
            </div>
          }
        </div>
        <div class="week-body">
          <div class="time-slots">
            @for (hour of [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23]; track hour) {
              <div class="time-row">
                <div class="time-label">
                  {{ hour === 0 ? '12 AM' : hour < 12 ? hour + ' AM' : hour === 12 ? '12 PM' : (hour - 12) + ' PM' }}
                </div>
                @for (date of getWeekDates(); track $index) {
                  <div
                    class="time-cell"
                    [class.today]="isToday(date)"
                    (dblclick)="openAddEventModal(date)"
                  >
                    @for (event of calendarService.getEventsForDate(date); track trackByEventId($index, event)) {
                      @if (getEventHour(event.startDate) === hour) {
                        <div
                          class="week-event"
                          [style.background-color]="event.color?.background"
                          [style.color]="event.color?.foreground"
                          (click)="openEventDetails(event); $event.stopPropagation()"
                        >
                          <span class="event-time-short">{{ event.startDate | date:'h:mm a' }}</span>
                          <span class="event-title-short">{{ event.title }}</span>
                        </div>
                      }
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- List View -->
    @if (currentView() === 'list') {
      <div class="list-view" @fadeIn>
        @if (events.length === 0) {
          <div class="empty-state">
            <i class="fas fa-calendar-plus"></i>
            <h3>No Events Yet</h3>
            <p>Create your first event to get started</p>
            <button class="create-btn" (click)="openAddEventModal()">
              <i class="fas fa-plus"></i>
              Create Event
            </button>
          </div>
        } @else {
          <div class="event-list-view">
            @for (event of events; track trackByEventId($index, event)) {
              <div class="list-event-icon" @slideIn (click)="openEventDetails(event)">
                <div class="event-icon-wrapper" [style.background-color]="event.color?.background">
                  @if (event.category === 'work') {
                    <i class="fas fa-briefcase"></i>
                  } @else if (event.category === 'personal') {
                    <i class="fas fa-user"></i>
                  } @else if (event.category === 'meeting') {
                    <i class="fas fa-users"></i>
                  } @else if (event.category === 'birthday') {
                    <i class="fas fa-birthday-cake"></i>
                  } @else if (event.category === 'holiday') {
                    <i class="fas fa-calendar-check"></i>
                  } @else if (event.category === 'reminder') {
                    <i class="fas fa-bell"></i>
                  } @else {
                    <i class="fas fa-calendar-alt"></i>
                  }
                </div>
                <div class="event-info">
                  <div class="event-title-row">
                    <h4 class="event-title">{{ event.title }}</h4>
                    <span class="event-category-badge" [style.background-color]="event.color?.background">
                      {{ getCategoryName(event.category || 'other') }}
                    </span>
                  </div>
                  <div class="event-meta">
                    <span class="event-meta-item">
                      <i class="fas fa-calendar"></i>
                      {{ formatEventDate(event.startDate) }}
                    </span>
                    <span class="event-meta-item">
                      <i class="fas fa-clock"></i>
                      {{ formatEventTime(event) }}
                    </span>
                    @if (event.location) {
                      <span class="event-meta-item">
                        <i class="fas fa-map-marker-alt"></i>
                        {{ event.location }}
                      </span>
                    }
                    @if (event.description) {
                      <span class="event-meta-item">
                        <i class="fas fa-align-left"></i>
                        {{ event.description }}
                      </span>
                    }
                  </div>
                </div>
                <div class="event-actions">
                  <button class="action-icon" (click)="openEditEventModal(event); $event.stopPropagation()" title="Edit">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="action-icon danger" (click)="deleteEvent(event); $event.stopPropagation()" title="Delete">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
  </main>
</div>

<!-- Event Modal -->
@if (showEventModal()) {
  <div class="modal-overlay" (click)="closeEventModal()">
    <div class="event-modal" @modalAnimation (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-header-content">
          <i class="fas fa-calendar-plus modal-header-icon"></i>
          <h2>{{ isEditing() ? 'Edit Event' : 'New Event' }}</h2>
        </div>
        <button class="close-btn" (click)="closeEventModal()" aria-label="Close modal">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label for="title">Event Title *</label>
          <input
            type="text"
            id="title"
            class="form-input"
            [(ngModel)]="eventForm.title"
            placeholder="Enter event title"
            autofocus
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              class="form-input"
              [(ngModel)]="eventForm.startDate"
            />
          </div>
          @if (!eventForm.allDay) {
            <div class="form-group">
              <label for="startTime">Start Time</label>
              <input
                type="time"
                id="startTime"
                class="form-input"
                [(ngModel)]="eventForm.startTime"
              />
            </div>
          }
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              class="form-input"
              [(ngModel)]="eventForm.endDate"
            />
          </div>
          @if (!eventForm.allDay) {
            <div class="form-group">
              <label for="endTime">End Time</label>
              <input
                type="time"
                id="endTime"
                class="form-input"
                [(ngModel)]="eventForm.endTime"
              />
            </div>
          }
        </div>

        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input type="checkbox" [(ngModel)]="eventForm.allDay" />
            <span class="checkmark"></span>
            All day event
          </label>
        </div>

        <!-- Toggle for advanced options -->
        <button
          type="button"
          class="toggle-advanced-btn"
          (click)="showAdvancedOptions.set(!showAdvancedOptions())"
        >
          <i class="fas" [class.fa-chevron-down]="!showAdvancedOptions()" [class.fa-chevron-up]="showAdvancedOptions()"></i>
          {{ showAdvancedOptions() ? 'Hide' : 'Show' }} more options
        </button>

        @if (showAdvancedOptions()) {
          <div class="advanced-options" @fadeIn>
            <div class="form-group">
              <label>Category</label>
              <div class="category-selector">
                @for (category of categories; track category.id) {
                  <button
                    type="button"
                    class="category-btn"
                    [class.active]="eventForm.category === category.id"
                    [style.--category-color]="category.color.background"
                    (click)="eventForm.category = category.id"
                  >
                    <span class="category-dot" [style.background-color]="category.color.background"></span>
                    {{ category.name }}
                  </button>
                }
              </div>
            </div>

            <div class="form-group">
              <label for="location">Location</label>
              <input
                type="text"
                id="location"
                class="form-input"
                [(ngModel)]="eventForm.location"
                placeholder="Add location"
              />
            </div>

            <div class="form-group">
              <label for="description">Description</label>
              <textarea
                id="description"
                class="form-input textarea"
                [(ngModel)]="eventForm.description"
                placeholder="Add description"
                rows="3"
              ></textarea>
            </div>

            <div class="form-group">
              <label for="reminder">Reminder</label>
              <select id="reminder" class="form-input" [(ngModel)]="eventForm.reminder">
                @for (option of reminderOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
            </div>
          </div>
        }
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" (click)="closeEventModal()">Cancel</button>
        <button class="btn btn-primary" (click)="saveEvent()">
          {{ isEditing() ? 'Save Changes' : 'Create Event' }}
        </button>
      </div>
    </div>
  </div>
}

<!-- Event Details Modal -->
@if (showEventDetails() && selectedEvent) {
  <div class="modal-overlay" (click)="closeEventDetails()">
    <div class="event-details-modal" @modalAnimation (click)="$event.stopPropagation()">
      <div class="details-header" [style.background-color]="selectedEvent.color?.background">
        <div class="header-content">
          <h2>{{ selectedEvent.title }}</h2>
          <span class="event-category-badge">{{ getCategoryName(selectedEvent.category || 'other') }}</span>
        </div>
        <button class="close-btn light" (click)="closeEventDetails()">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="details-body">
        <div class="detail-item">
          <i class="fas fa-calendar"></i>
          <div class="detail-content">
            <span class="detail-label">Date</span>
            <span class="detail-value">{{ formatEventDate(selectedEvent.startDate) }}</span>
          </div>
        </div>
        <div class="detail-item">
          <i class="fas fa-clock"></i>
          <div class="detail-content">
            <span class="detail-label">Time</span>
            <span class="detail-value">{{ formatEventTime(selectedEvent) }}</span>
          </div>
        </div>
        @if (selectedEvent.location) {
          <div class="detail-item">
            <i class="fas fa-map-marker-alt"></i>
            <div class="detail-content">
              <span class="detail-label">Location</span>
              <span class="detail-value">{{ selectedEvent.location }}</span>
            </div>
          </div>
        }
        @if (selectedEvent.description) {
          <div class="detail-item description">
            <i class="fas fa-align-left"></i>
            <div class="detail-content">
              <span class="detail-label">Description</span>
              <p class="detail-value">{{ selectedEvent.description }}</p>
            </div>
          </div>
        }
        @if (selectedEvent.reminder !== undefined) {
          <div class="detail-item">
            <i class="fas fa-bell"></i>
            <div class="detail-content">
              <span class="detail-label">Reminder</span>
              <span class="detail-value">
                {{ getReminderLabel(selectedEvent!.reminder) }}
              </span>
            </div>
          </div>
        }
      </div>
      <div class="details-footer">
        <button class="btn btn-secondary" (click)="showEventExportOptions(selectedEvent)">
          <i class="fas fa-calendar-plus"></i>
          Add to Calendar
        </button>
        <button class="btn btn-secondary" (click)="openEditEventModal(selectedEvent)">
          <i class="fas fa-edit"></i>
          Edit
        </button>
        <button class="btn btn-danger" (click)="deleteEvent(selectedEvent)">
          <i class="fas fa-trash"></i>
          Delete
        </button>
      </div>
    </div>
  </div>
}
